import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import InfraNav from './components/InfraNav';
import InfraFooter from './components/InfraFooter';
import InfraCTA from './components/InfraCTA';
import './infrastructure.css';

export default function InfrastructureComply() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="infra-page" data-testid="infrastructure-comply">
      <InfraNav transparent={false} />

      {/* ══════ HERO ══════ */}
      <div className="infra-product-hero">
        <div className="infra-product-hero-text">
          <div className="infra-product-eyebrow" style={{ color: 'var(--pebbles-gray-500)' }}>COMPLY</div>
          <h1 className="infra-product-headline">Stay compliant in every jurisdiction.</h1>
          <p className="infra-product-desc">
            Identity verification, transaction monitoring, audit trails, and regulatory reporting — built for CBN compliance and beyond.
          </p>
          <div className="infra-product-buttons">
            <Link to="/login" className="infra-btn-primary" data-testid="comply-get-started">Get started</Link>
            <a href="mailto:hello@pebbles.financial" className="infra-btn-secondary">Contact sales</a>
          </div>
        </div>
        <div className="infra-product-hero-mockup">
          <div className="infra-feature-card">
            <div className="infra-feature-card-inner">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {['KYC tier 2 verified', 'AML screening passed', 'Transaction limits enforced', 'Audit trail exported', 'Regulatory report filed'].map((t, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 22, height: 22, borderRadius: '50%', background: 'rgba(29,158,117,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <span style={{ fontSize: 11, color: 'var(--pebbles-green)' }}>&#10003;</span>
                    </div>
                    <span style={{ fontSize: 13, color: 'var(--pebbles-dark)' }}>{t}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ══════ FEATURE 1: KYC ══════ */}
      <div className="infra-feature">
        <div className="infra-feature-inner">
          <div className="infra-feature-text">
            <span className="infra-feature-label" style={{ background: 'var(--pebbles-gray-100)', color: 'var(--pebbles-gray-500)' }}>KYC</span>
            <h3 className="infra-feature-title">Identity verification at every tier</h3>
            <p className="infra-feature-desc">
              BVN verification, NIN validation, document upload, and liveness checks. Tiered KYC levels with progressive unlocking of transaction limits.
            </p>
            <ul className="infra-feature-bullets infra-check-gray">
              <li>BVN and NIN verification</li>
              <li>Document upload and validation</li>
              <li>Tiered KYC: Level 1 → Level 2 → Level 3</li>
              <li>Progressive limit unlocking</li>
            </ul>
          </div>
          <div className="infra-feature-mockup">
            <div className="infra-feature-card">
              <div className="infra-feature-card-inner" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--pebbles-gray-500)', letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: 4 }}>KYC Tiers</div>
                {[
                  { tier: 'Level 1', req: 'Phone + email', limit: '₦50,000/day', status: 'Auto-verified' },
                  { tier: 'Level 2', req: 'BVN + NIN', limit: '₦500,000/day', status: 'Verified' },
                  { tier: 'Level 3', req: 'Document + liveness', limit: 'Unlimited', status: 'Enhanced' },
                ].map((k, i) => (
                  <div key={i} className="infra-data-row" style={{ borderRadius: 8, border: '1px solid var(--pebbles-gray-100)', padding: 12 }}>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--pebbles-dark)' }}>{k.tier}</div>
                      <div style={{ fontSize: 10, color: 'var(--pebbles-gray-400)' }}>{k.req}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--pebbles-dark)' }}>{k.limit}</div>
                      <div style={{ fontSize: 9, color: 'var(--pebbles-green)' }}>{k.status}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ══════ FEATURE 2: AML ══════ */}
      <div className="infra-feature infra-feature--reverse infra-feature--gray">
        <div className="infra-feature-inner">
          <div className="infra-feature-text">
            <span className="infra-feature-label" style={{ background: 'var(--pebbles-gray-100)', color: 'var(--pebbles-gray-500)' }}>AML</span>
            <h3 className="infra-feature-title">Anti-money laundering, built in</h3>
            <p className="infra-feature-desc">
              Real-time transaction screening against sanctions lists, PEP databases, and adverse media. Suspicious activity is flagged and escalated automatically.
            </p>
            <ul className="infra-feature-bullets infra-check-gray">
              <li>Real-time sanctions screening</li>
              <li>PEP (Politically Exposed Persons) checks</li>
              <li>Automated suspicious activity reports</li>
              <li>Case management for compliance teams</li>
            </ul>
          </div>
          <div className="infra-feature-mockup">
            <div className="infra-feature-card">
              <div className="infra-feature-card-inner">
                <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--pebbles-gray-500)', letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: 12 }}>Screening Results</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {[
                    { check: 'Global sanctions', result: 'Clear', color: 'var(--pebbles-green)' },
                    { check: 'PEP database', result: 'Clear', color: 'var(--pebbles-green)' },
                    { check: 'Adverse media', result: 'Clear', color: 'var(--pebbles-green)' },
                    { check: 'Velocity check', result: 'Passed', color: 'var(--pebbles-green)' },
                  ].map((s, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px', background: '#fff', borderRadius: 8, border: '1px solid var(--pebbles-gray-100)' }}>
                      <span style={{ fontSize: 12, color: 'var(--pebbles-dark)' }}>{s.check}</span>
                      <span style={{ fontSize: 10, fontWeight: 500, color: s.color, background: `${s.color}15`, padding: '2px 8px', borderRadius: 99 }}>{s.result}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ══════ FEATURE 3: Audit ══════ */}
      <div className="infra-feature">
        <div className="infra-feature-inner">
          <div className="infra-feature-text">
            <span className="infra-feature-label" style={{ background: 'var(--pebbles-gray-100)', color: 'var(--pebbles-gray-500)' }}>Audit</span>
            <h3 className="infra-feature-title">Complete audit trail, always</h3>
            <p className="infra-feature-desc">
              Every action — every login, every transfer, every approval — is logged with timestamp, actor, IP, and details. Immutable, exportable, and searchable.
            </p>
            <ul className="infra-feature-bullets infra-check-gray">
              <li>Immutable event log</li>
              <li>Actor, IP, and timestamp on every entry</li>
              <li>Full-text search across all events</li>
              <li>CSV and PDF export</li>
            </ul>
          </div>
          <div className="infra-feature-mockup">
            <div className="infra-feature-card">
              <div className="infra-feature-card-inner" style={{ overflowX: 'auto' }}>
                <table className="infra-ledger">
                  <thead>
                    <tr>
                      <th>Time</th>
                      <th>Actor</th>
                      <th>Action</th>
                      <th>Detail</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr><td>10:42</td><td>admin@thco.ng</td><td>payroll.approved</td><td>March 2026 cycle</td></tr>
                    <tr><td>10:38</td><td>victoria@thco.ng</td><td>payroll.submitted</td><td>42 employees</td></tr>
                    <tr><td>09:15</td><td>system</td><td>reconciliation.run</td><td>0 discrepancies</td></tr>
                    <tr><td>08:00</td><td>ayo@thco.ng</td><td>transfer.initiated</td><td>₦50,000 → GTBank</td></tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ══════ FEATURE 4: Limits & Reports ══════ */}
      <div className="infra-feature infra-feature--reverse infra-feature--gray">
        <div className="infra-feature-inner">
          <div className="infra-feature-text">
            <span className="infra-feature-label" style={{ background: 'var(--pebbles-gray-100)', color: 'var(--pebbles-gray-500)' }}>Reports</span>
            <h3 className="infra-feature-title">Regulatory reporting, automated</h3>
            <p className="infra-feature-desc">
              Generate CBN-compliant reports, tax filings, and regulatory submissions. Scheduled or on-demand, with templated formats for every jurisdiction.
            </p>
            <ul className="infra-feature-bullets infra-check-gray">
              <li>CBN regulatory reports</li>
              <li>PAYE and tax remittance schedules</li>
              <li>Transaction limit enforcement</li>
              <li>Configurable daily, weekly, monthly limits</li>
            </ul>
          </div>
          <div className="infra-feature-mockup">
            <div className="infra-feature-card">
              <div className="infra-feature-card-inner" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--pebbles-gray-500)', letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: 4 }}>Generated Reports</div>
                {[
                  { name: 'PAYE Remittance — March 2026', date: 'Mar 7, 2026', status: 'Filed' },
                  { name: 'Transaction Summary Q1 2026', date: 'Mar 1, 2026', status: 'Generated' },
                  { name: 'AML Compliance Report', date: 'Feb 28, 2026', status: 'Filed' },
                  { name: 'Annual Tax Report 2025', date: 'Jan 31, 2026', status: 'Filed' },
                ].map((r, i) => (
                  <div key={i} className="infra-data-row" style={{ borderRadius: 8, border: '1px solid var(--pebbles-gray-100)' }}>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--pebbles-dark)' }}>{r.name}</div>
                      <div style={{ fontSize: 9, color: 'var(--pebbles-gray-400)' }}>{r.date}</div>
                    </div>
                    <span className="infra-badge infra-badge-green">{r.status}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <InfraCTA />
      <InfraFooter />
    </div>
  );
}
